/**
 * Flow 5 — validate. The platform's own `validate_only` run.
 *
 * The dry run is mandatory before any live mutate, and errors here are the *only*
 * legitimate reason to bother a user with asset-level fixes: they are zero-cost
 * findings reported against exact document paths, which the review screen can jump to.
 */
import { useDryRun } from '@/api/hooks'
import type { AdAccount, ValidationReport } from '@/api/types'
import { formatDateTime, formatNumber } from '@/lib/format'
import { Badge, Button, Cap, EmptyState, ErrorNotice, KeyValue, Panel, Spinner } from '@/components/ui'

export function DryRunPanel({
  blueprintId,
  accounts,
  selectedAccountId,
  onSelectAccount,
  report,
  onReport,
  onInspectPath,
}: {
  blueprintId: string
  accounts: AdAccount[]
  selectedAccountId: string | null
  onSelectAccount: (accountId: string) => void
  report: ValidationReport | null
  onReport: (accountId: string, report: ValidationReport) => void
  onInspectPath: (path: string) => void
}) {
  const dryRun = useDryRun(blueprintId)
  const errors = report?.errors ?? []
  const warnings = report?.warnings ?? []

  async function run() {
    if (!selectedAccountId) return
    const result = await dryRun.mutateAsync(selectedAccountId)
    onReport(selectedAccountId, result)
  }

  return (
    <div className="flex flex-col gap-4" data-testid="dry-run-panel">
      <Panel
        title="Dry run"
        subtitle="validate_only against the platform. Free to run, and the last gate before anything live happens."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="dry-run-account" className="label-cap">
              Ad account
            </label>
            <select
              id="dry-run-account"
              data-testid="dry-run-account"
              value={selectedAccountId ?? ''}
              onChange={(event) => onSelectAccount(event.target.value)}
              className="rounded-md border border-ink-600 bg-ink-850 px-2.5 py-1.5 text-sm text-mist-100"
            >
              <option value="">Select an account…</option>
              {accounts.map((account) => (
                <option key={`${account.platform}:${account.external_id}`} value={account.external_id}>
                  {account.platform} · {account.name}
                  {account.conversion_tracking_ok ? '' : ' (no conversion tracking)'}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            data-testid="run-dry-run"
            disabled={!selectedAccountId || dryRun.isPending}
            onClick={run}
          >
            {dryRun.isPending ? 'Validating…' : 'Run dry run'}
          </Button>
          {dryRun.isPending ? <Spinner label="Waiting for validate_only…" /> : null}
        </div>
        {dryRun.isError ? <div className="mt-3"><ErrorNotice error={dryRun.error} /></div> : null}
      </Panel>

      {report ? (
        <Panel
          testId="dry-run-report"
          title={
            <div className="flex items-center gap-2">
              <h2 className="text-[13px] font-semibold text-mist-100">Dry-run report</h2>
              <Badge tone={report.ok ? 'ok' : 'danger'} testId="dry-run-status">
                {report.ok ? 'accepted' : 'rejected'}
              </Badge>
              <Badge tone="neutral">{report.platform}</Badge>
            </div>
          }
          subtitle={report.raw_summary ?? undefined}
        >
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KeyValue label="Checked entities">
              <span className="num">{formatNumber(report.checked_entities)}</span>
            </KeyValue>
            <KeyValue label="Estimated ops">
              <span className="num">{formatNumber(report.estimated_operations ?? 0)}</span>
            </KeyValue>
            <KeyValue label="Errors">
              <span className="num">{errors.length}</span>
            </KeyValue>
            <KeyValue label="Ran at">
              <span className="num">{formatDateTime(report.generated_at)}</span>
            </KeyValue>
          </div>

          {errors.length > 0 ? (
            <div className="mt-4">
              <div className="flex items-center gap-2">
                <Badge tone="danger">push refused</Badge>
                <span className="text-[12px] text-mist-300">
                  These must be fixed and the dry run re-run. Nothing was pushed.
                </span>
              </div>
              <ul className="mt-2 flex flex-col gap-2" data-testid="dry-run-errors">
                {errors.map((error, index) => (
                  <li
                    key={`${error.code}-${index}`}
                    className="rounded-md border border-signal-danger/40 bg-signal-danger/5 px-3 py-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="num text-[11px] font-semibold text-signal-danger">{error.code}</span>
                      {error.field_path ? (
                        <button
                          type="button"
                          className="num text-[11px] text-forge-300 underline decoration-dotted hover:text-forge-400"
                          onClick={() => onInspectPath(error.field_path!)}
                          data-testid="inspect-path"
                        >
                          {error.field_path}
                        </button>
                      ) : null}
                      {error.retryable ? <Badge tone="warn">retryable</Badge> : null}
                    </div>
                    <p className="mt-1 text-[12.5px] text-mist-200">{error.message}</p>
                    {error.help_url ? (
                      <a
                        href={error.help_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mt-1 inline-block text-[11.5px] text-mist-400 underline decoration-dotted"
                      >
                        platform policy reference
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mt-3 text-[12.5px] text-signal-ok" data-testid="dry-run-clean">
              No errors. The platform would accept this mutate — the push can proceed.
            </p>
          )}

          {warnings.length > 0 ? (
            <div className="mt-4">
              <Cap>Warnings · {warnings.length}</Cap>
              <ul className="mt-1.5 flex flex-col gap-1.5" data-testid="dry-run-warnings">
                {warnings.map((warning, index) => (
                  <li key={`${warning.code}-${index}`} className="flex gap-2 text-[12px] text-mist-300">
                    <span aria-hidden="true" className="text-signal-warn">
                      ▲
                    </span>
                    <span>
                      <span className="num mr-1.5 text-[11px] text-mist-500">{warning.code}</span>
                      {warning.message}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Panel>
      ) : (
        <EmptyState
          title="No dry run yet for this account"
          body="A passing dry run is a hard precondition of the push: AdForge refuses to send anything live that the platform has not already accepted."
        />
      )}
    </div>
  )
}
